import { Table } from '@radix-ui/themes';
import { ITargetProps } from 'components/common/training-data-table/common';
import { DeltaCell } from 'components/common/training-data-table/delta-cell';
import { MachineContext } from 'contexts/machine.context';
import { AVG_METHOD } from 'enums/pitches';
import { t } from 'i18next';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import {
  IBallState,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { compact } from 'lodash';
import { useContext, useMemo } from 'react';

export const DataTableAverageRow = (props: {
  priority: BuildPriority;
  targetBS: IBallState;
  targetBreaks?: ITrajektRefBreak;
  targetTraj: ITrajectory;
  actualShots: IMachineShot[];
  discardedShots?: string[];
}) => {
  const { unitFactors } = useContext(MachineContext);

  const deltas = useMemo((): ITargetProps[] => {
    const validShots = props.actualShots.filter(
      (s) => !props.discardedShots?.includes(s._id)
    );

    const validTrajs = compact(validShots.map((s) => s.traj));

    const avgTraj =
      validTrajs.length > 0
        ? (AVG_METHOD.fn(validTrajs) as ITrajectory)
        : undefined;

    const output: ITargetProps[] = [
      {
        target:
          unitFactors.fromSpeedMph *
          TrajHelper.getSpeedFromTraj(props.targetTraj),
        value: avgTraj
          ? unitFactors.fromSpeedMph * TrajHelper.getSpeedFromTraj(avgTraj)
          : undefined,
        precision: 1,
      },
    ];

    switch (props.priority) {
      case BuildPriority.Breaks: {
        const validBreaks = validShots.map((s) =>
          TrajHelper.getBreaksFromShot(s)
        );

        const avgBreaks =
          validBreaks.length > 0
            ? (AVG_METHOD.fn(validBreaks) as ITrajektRefBreak)
            : undefined;

        output.push(
          {
            target:
              (props.targetBreaks?.zInches ?? 0) * unitFactors.fromBreaksIn,
            value:
              avgBreaks && !isNaN(avgBreaks.zInches)
                ? avgBreaks.zInches * unitFactors.fromBreaksIn
                : undefined,
            precision: 1,
          },
          {
            target:
              -1 *
              (props.targetBreaks?.xInches ?? 0) *
              unitFactors.fromBreaksIn,
            value:
              avgBreaks && !isNaN(avgBreaks.xInches)
                ? -1 * avgBreaks.xInches * unitFactors.fromBreaksIn
                : undefined,
            precision: 1,
          }
        );
        break;
      }

      case BuildPriority.Default:
      case BuildPriority.Spins:
      default: {
        const validBss = validShots
          .map((s) => s.bs)
          .filter((s) => s) as IBallState[];

        const avgBs =
          validBss.length > 0
            ? (AVG_METHOD.fn(validBss) as IBallState)
            : undefined;

        output.push(
          {
            target: props.targetBS.wx,
            value: avgBs?.wx,
            precision: 0,
          },
          {
            target: props.targetBS.wy,
            value: avgBs?.wy,
            precision: 0,
          },
          {
            target: props.targetBS.wz,
            value: avgBs?.wz,
            precision: 0,
          }
        );
        break;
      }
    }

    return output;
  }, [
    unitFactors,
    props.actualShots,
    props.discardedShots,
    props.priority,
    props.targetBreaks,
    props.targetBS,
    props.targetTraj,
  ]);

  return (
    <Table.Row>
      <Table.Cell>{t(AVG_METHOD.label)}</Table.Cell>

      <Table.Cell>{/* video playback placeholder */}</Table.Cell>

      {deltas.map((v, i) => (
        <DeltaCell key={i} {...v} />
      ))}

      <Table.Cell>{/* actions placeholder */}</Table.Cell>
    </Table.Row>
  );
};
